import React, { useEffect, useMemo, useRef, useState } from "react";
import { PhotoIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "../../components/button";

// Matches the server's limits for message photos.
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_PHOTOS = 10;

// Photo grid for a message. While composing, picked files are held in
// `pendingFiles` and sent with the message. A sent message's `photos` are
// shown read-only: sent messages can't change.
function MessagePhotos({ photos, pendingFiles, onPendingChange }) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const composing = !photos;

  // Previews for files that aren't sent yet; revoked when the list changes.
  const previews = useMemo(
    () => (pendingFiles || []).map((file) => URL.createObjectURL(file)),
    [pendingFiles]
  );
  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews]);

  const handleFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    setError("");
    const tooBig = files.find((f) => f.size > MAX_BYTES);
    if (tooBig) {
      setError(`${tooBig.name} is too large (max 10MB)`);
      return;
    }
    const next = [...(pendingFiles || []), ...files];
    if (next.length > MAX_PHOTOS) {
      setError(`Up to ${MAX_PHOTOS} photos`);
      return;
    }
    onPendingChange(next);
  };

  const tiles = composing
    ? (pendingFiles || []).map((file, index) => ({
        key: `${file.name}-${index}`,
        src: previews[index],
        name: file.name,
        remove: () => onPendingChange(pendingFiles.filter((_, i) => i !== index)),
      }))
    : photos.map((photo) => ({
        key: photo.id,
        src: photo.url,
        name: photo.file_name,
        href: photo.url,
      }));

  if (!composing && tiles.length === 0) return null;

  return (
    <div>
      {composing && (
        <div className="flex items-center justify-between mb-1">
          <span className="block text-sm font-medium text-gray-600 dark:text-gray-300">
            Photos
          </span>
          <Button
            variant="secondary"
            size="sm"
            icon={PlusIcon}
            onClick={() => inputRef.current?.click()}
          >
            Add photo
          </Button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFiles}
      />
      {error && <p className="mb-2 text-sm text-danger">{error}</p>}
      {tiles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-4">
          <PhotoIcon className="h-6 w-6 text-gray-300 dark:text-gray-600" />
          <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">No photos yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {tiles.map((tile) => (
            <div
              key={tile.key}
              className="group relative aspect-square overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800"
            >
              <a href={tile.href} target="_blank" rel="noreferrer">
                <img src={tile.src} alt={tile.name} className="h-full w-full object-cover" />
              </a>
              {tile.remove && (
                <button
                  type="button"
                  onClick={tile.remove}
                  aria-label={`Remove ${tile.name}`}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { MessagePhotos };
